/**
 * Slide-over panel showing full CV-JD analysis detail.
 * Opened by clicking a CV card in MatchingHistoryView or MatchingCreateView preview.
 */

import { X, User, FileText, CheckCircle, XCircle, Lightbulb, Loader2, AlertCircle, RefreshCw } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { AnalysisStatus, AnalysisMetadata } from '../../types';

export interface AnalysisPanelState {
  cvName: string;
  jdName: string;
  cvId: string;
  jdId: string;
  status: AnalysisStatus;
  score: number | null;
  metadata: AnalysisMetadata | null;
}

interface Props extends AnalysisPanelState {
  onClose: () => void;
  onRetry?: (cvId: string, jdId: string) => void;
  isRetrying?: boolean;
}

// ── Score ring ───────────────────────────────────────────────────────────────

function ScoreRing({ score }: { score: number }) {
  const color =
    score >= 80 ? '#22c55e' : score >= 60 ? '#eab308' : '#ef4444';
  const r = 40;
  const circ = 2 * Math.PI * r;
  const dash = (score / 100) * circ;
  return (
    <div className="relative w-24 h-24 flex items-center justify-center">
      <svg width="96" height="96" className="-rotate-90">
        <circle cx="48" cy="48" r={r} fill="none" stroke="#e5e7eb" strokeWidth="8" />
        <circle
          cx="48" cy="48" r={r} fill="none"
          stroke={color} strokeWidth="8"
          strokeDasharray={`${dash} ${circ}`}
          strokeLinecap="round"
        />
      </svg>
      <span className="absolute text-2xl font-bold tabular-nums" style={{ color }}>{score}</span>
    </div>
  );
}

// ── Category bar ─────────────────────────────────────────────────────────────

function CategoryBar({ label, value }: { label: string; value: number }) {
  const bar =
    value >= 80 ? 'bg-green-400' : value >= 60 ? 'bg-yellow-400' : 'bg-red-400';
  return (
    <div>
      <div className="flex justify-between text-xs mb-1">
        <span className="text-gray-600">{label}</span>
        <span className="font-semibold tabular-nums">{value}%</span>
      </div>
      <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
        <div className={`h-full rounded-full ${bar}`} style={{ width: `${value}%` }} />
      </div>
    </div>
  );
}

// ── Main panel ────────────────────────────────────────────────────────────────

export function AnalysisDetailPanel({ cvName, jdName, cvId, jdId, status, metadata, onClose, onRetry, isRetrying }: Props) {
  const { t } = useTranslation('ats');
  const isCompleted = status === 'completed' && metadata != null;

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/20" onClick={onClose} />

      {/* Panel */}
      <div className="relative w-[400px] max-w-full bg-white shadow-2xl flex flex-col h-full">

        {/* Header */}
        <div className="flex items-start gap-3 p-4 border-b border-gray-200 flex-shrink-0">
          <div className="flex-1 min-w-0">
            <h2 className="text-sm font-semibold text-gray-900">{t('analysis.title')}</h2>
            <div className="flex items-center gap-1 mt-1 text-xs text-gray-500 min-w-0">
              <User className="h-3 w-3 flex-shrink-0 text-gray-400" />
              <span className="truncate font-medium text-gray-700">{cvName}</span>
              <span className="flex-shrink-0 px-1">→</span>
              <FileText className="h-3 w-3 flex-shrink-0 text-orange-400" />
              <span className="truncate text-gray-600">{jdName}</span>
            </div>
          </div>
          <button
            onClick={onClose}
            className="flex-shrink-0 p-1 rounded-md hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-4 space-y-5">

          {/* Pending */}
          {status === 'pending' && (
            <div className="flex flex-col items-center justify-center py-16 gap-3 text-gray-400">
              <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center">
                <Loader2 className="h-6 w-6 text-gray-300 animate-spin" />
              </div>
              <p className="text-sm">{t('analysis.waitingToAnalyze')}</p>
            </div>
          )}

          {/* Analyzing */}
          {status === 'analyzing' && (
            <div className="flex flex-col items-center justify-center py-16 gap-3 text-blue-500">
              <div className="w-12 h-12 rounded-full bg-blue-50 flex items-center justify-center">
                <Loader2 className="h-6 w-6 animate-spin" />
              </div>
              <p className="text-sm font-medium">{t('analysis.aiAnalyzing')}</p>
              <p className="text-xs text-gray-400">{t('analysis.resultsAutomatic')}</p>
            </div>
          )}

          {/* Error */}
          {status === 'error' && (
            <div className="flex flex-col items-center justify-center py-16 gap-3 text-red-500">
              <div className="w-12 h-12 rounded-full bg-red-50 flex items-center justify-center">
                <AlertCircle className="h-6 w-6" />
              </div>
              <p className="text-sm font-medium">{t('analysis.analysisFailed')}</p>
              {metadata && (
                <p className="text-xs text-gray-400 text-center px-4">
                  {(metadata as unknown as { error?: { message?: string } }).error?.message ?? t('analysis.unknownError')}
                </p>
              )}
              {onRetry && (
                <button
                  onClick={() => onRetry(cvId, jdId)}
                  disabled={isRetrying}
                  className="mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 rounded-md transition-colors"
                >
                  <RefreshCw className={`h-3 w-3 ${isRetrying ? 'animate-spin' : ''}`} />
                  {isRetrying ? t('analysis.sending') : t('analysis.reAnalyze')}
                </button>
              )}
            </div>
          )}

          {/* Completed */}
          {isCompleted && (
            <>
              {/* Overall score */}
              <div className="flex flex-col items-center gap-1 py-2">
                <ScoreRing score={metadata!.overall_match_score} />
                <span className="text-xs font-medium text-gray-500 mt-1">{t('analysis.overallMatchScore')}</span>
              </div>

              {/* Category scores */}
              {metadata!.category_scores && (
                <div className="space-y-3">
                  <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wide">{t('analysis.categoryScores')}</h3>
                  <CategoryBar label={t('analysis.skillsMatch')}  value={metadata!.category_scores.skills_match} />
                  <CategoryBar label={t('analysis.experience')}    value={metadata!.category_scores.experience_match} />
                  <CategoryBar label={t('analysis.educationMatch')}     value={metadata!.category_scores.education_match} />
                  <CategoryBar label={t('analysis.certification')}  value={metadata!.category_scores.certification_match ?? metadata!.category_scores.cultural_fit ?? 0} />
                </div>
              )}

              {/* Strengths */}
              {(metadata!.strengths?.length ?? 0) > 0 && (
                <div>
                  <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">{t('analysis.strengths')}</h3>
                  <ul className="space-y-1.5">
                    {metadata!.strengths.map((s, i) => (
                      <li key={i} className="flex items-start gap-2 text-xs text-gray-700">
                        <CheckCircle className="h-3.5 w-3.5 text-green-500 flex-shrink-0 mt-0.5" />
                        <span>{s}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Gaps */}
              {(metadata!.gaps?.length ?? 0) > 0 && (
                <div>
                  <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">{t('analysis.gaps')}</h3>
                  <ul className="space-y-1.5">
                    {metadata!.gaps.map((g, i) => (
                      <li key={i} className="flex items-start gap-2 text-xs text-gray-700">
                        <XCircle className="h-3.5 w-3.5 text-red-400 flex-shrink-0 mt-0.5" />
                        <span>{g}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Recommendations */}
              {(metadata!.recommendations?.length ?? 0) > 0 && (
                <div>
                  <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">{t('analysis.recommendations')}</h3>
                  <ul className="space-y-1.5">
                    {metadata!.recommendations.map((r, i) => (
                      <li key={i} className="flex items-start gap-2 text-xs text-gray-700">
                        <Lightbulb className="h-3.5 w-3.5 text-blue-400 flex-shrink-0 mt-0.5" />
                        <span>{r}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Key Insights */}
              {metadata!.key_insights && (
                <div>
                  <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">{t('analysis.keyInsights')}</h3>

                  {(metadata!.key_insights.matching_skills?.length ?? 0) > 0 && (
                    <div className="mb-3">
                      <p className="text-[10px] font-medium text-gray-500 uppercase tracking-wide mb-1.5">{t('analysis.matchingSkills')}</p>
                      <div className="flex flex-wrap gap-1">
                        {metadata!.key_insights.matching_skills.map((s, i) => (
                          <span key={i} className="px-1.5 py-0.5 bg-green-50 text-green-700 text-[10px] rounded font-medium">
                            {s}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {(metadata!.key_insights.missing_skills?.length ?? 0) > 0 && (
                    <div className="mb-3">
                      <p className="text-[10px] font-medium text-gray-500 uppercase tracking-wide mb-1.5">{t('analysis.missingSkills')}</p>
                      <div className="flex flex-wrap gap-1">
                        {metadata!.key_insights.missing_skills.map((s, i) => (
                          <span key={i} className="px-1.5 py-0.5 bg-red-50 text-red-600 text-[10px] rounded font-medium">
                            {s}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {metadata!.key_insights.relevant_experience && (
                    <div className="mb-2">
                      <p className="text-[10px] font-medium text-gray-500 uppercase tracking-wide mb-1">{ t('analysis.experienceNote')}</p>
                      <p className="text-xs text-gray-700">{metadata!.key_insights.relevant_experience}</p>
                    </div>
                  )}

                  {metadata!.key_insights.education_relevance && (
                    <div>
                      <p className="text-[10px] font-medium text-gray-500 uppercase tracking-wide mb-1">{t('analysis.educationNote')}</p>
                      <p className="text-xs text-gray-700">{metadata!.key_insights.education_relevance}</p>
                    </div>
                  )}
                </div>
              )}

              {/* Footer metadata */}
              {metadata!._metadata && (
                <div className="pt-3 border-t border-gray-100 text-[10px] text-gray-400 space-y-0.5">
                  <p>{t('analysis.type')} {metadata!._metadata.analysis_type}</p>
                  <p>{t('analysis.model')} {metadata!._metadata.model}</p>
                  {metadata!._metadata.analyzed_at && (
                    <p>{t('analysis.analyzed')} {new Date(metadata!._metadata.analyzed_at).toLocaleString()}</p>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
